import React, { useState, useEffect } from 'react';
import {
  Box, Container, Stepper, Step, StepLabel, Button, Typography, Paper,
  useTheme, useMediaQuery, alpha, CircularProgress, Alert, Snackbar,
  LinearProgress, Tooltip, IconButton, Card, CardContent, Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  Save as SaveIcon,
  Help as HelpIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { doctorOnboardingApi } from '../../services/api';
import { toast } from 'react-hot-toast';

// Step components
import BasicInfoStep from './steps/BasicInfoStep.js';
import DocumentsStep from './steps/DocumentsStep.js';
import ClinicSetupStep from './steps/ClinicSetupStep.js';
import AvailabilityStep from './steps/AvailabilityStep.js';
import BillingStep from './steps/BillingStep.js';
import VerificationStatusStep from './steps/VerificationStatusStep.js';

// UI helpers
const steps = [
  'Basic Info',
  'Documents',
  'Clinic Setup',
  'Availability',
  'Billing',
];

const stepKeys = [
  'basic_info',
  'documents',
  'clinic',
  'availability',
  'billing',
];

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0, height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '.MuiStepLabel-root .Mui-completed': { color: theme.palette.success.main },
  '.MuiStepLabel-root .Mui-active': { color: theme.palette.primary.main },
}));

const EnhancedDoctorOnboarding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [onboardingState, setOnboardingState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load onboarding state
  useEffect(() => {
    loadOnboardingState();
  }, []);

  const loadOnboardingState = async () => {
    try {
      setLoading(true);
      const response = await doctorOnboardingApi.getState();
      const state = response.data;
      setOnboardingState(state);
      
      // Set active step based on current step
      const stepIndex = stepKeys.indexOf(state.current_step);
      if (stepIndex !== -1) {
        setActiveStep(stepIndex);
      }
    } catch (error) {
      console.error('Error loading onboarding state:', error);
      toast.error('Failed to load onboarding state');
    } finally {
      setLoading(false);
    }
  };

  // Handle step completion
  const handleStepComplete = async (stepData) => {
    try {
      setSubmitting(true);
      
      // Submit step data based on current step
      switch (activeStep) {
        case 0: // Basic Info
          await doctorOnboardingApi.submitBasic(stepData);
          break;
        case 1: // Documents
          await doctorOnboardingApi.submitDocuments(stepData);
          break;
        case 2: // Clinic Setup
          await doctorOnboardingApi.submitClinic(stepData);
          break;
        case 3: // Availability
          await doctorOnboardingApi.submitAvailability(stepData);
          break;
        case 4: // Billing
          await doctorOnboardingApi.submitBilling(stepData);
          break;
      }

      // Advance to next step
      const nextStep = stepKeys[activeStep + 1];
      if (nextStep) {
        await doctorOnboardingApi.advance(nextStep);
        setActiveStep(activeStep + 1);
      }

      // Reload state
      await loadOnboardingState();
      
      toast.success('Step completed successfully');
      setSnackbar({ open: true, message: 'Step completed successfully', severity: 'success' });
    } catch (error) {
      console.error('Error completing step:', error);
      toast.error('Failed to complete step');
      setSnackbar({ open: true, message: 'Failed to complete step', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  // Check if step is completed
  const isStepCompleted = (stepIndex) => {
    if (!onboardingState) return false;
    return onboardingState.completed_steps.includes(stepKeys[stepIndex]);
  };

  // Check if step is accessible
  const isStepAccessible = (stepIndex) => {
    if (stepIndex === 0) return true;
    return isStepCompleted(stepIndex - 1);
  };

  // Render step content
  const renderStepContent = () => {
    if (onboardingState?.is_completed) {
      return <VerificationStatusStep state={onboardingState} />;
    }

    const stepProps = {
      formData,
      setFormData,
      onSubmit: handleStepComplete,
      submitting,
      isCompleted: isStepCompleted(activeStep),
    };

    switch (activeStep) {
      case 0:
        return <BasicInfoStep {...stepProps} />;
      case 1:
        return <DocumentsStep {...stepProps} />;
      case 2:
        return <ClinicSetupStep {...stepProps} />;
      case 3:
        return <AvailabilityStep {...stepProps} />;
      case 4:
        return <BillingStep {...stepProps} />;
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Doctor Onboarding
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete your profile to start accepting patients
        </Typography>
      </Box>

      {/* Progress Bar */}
      {onboardingState && (
        <Box mb={3}>
          <LinearProgress
            variant="determinate"
            value={(onboardingState.completed_steps.length / stepKeys.length) * 100}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" color="text.secondary" mt={1}>
            {onboardingState.completed_steps.length} of {stepKeys.length} steps completed
          </Typography>
        </Box>
      )}

      {/* Stepper */}
      {!onboardingState?.is_completed && (
        <StyledStepper activeStep={activeStep} alternativeLabel={!isMobile}>
          {steps.map((label, index) => (
            <Step key={label} completed={isStepCompleted(index)}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: completed
                        ? 'success.main'
                        : active
                        ? 'primary.main'
                        : 'grey.300',
                      color: completed || active ? 'white' : 'grey.600',
                    }}
                  >
                    {completed ? <CheckCircleIcon /> : index + 1}
                  </Box>
                )}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </StyledStepper>
      )}

      {/* Step Content */}
      <StyledPaper>
        {renderStepContent()}
      </StyledPaper>

      {/* Navigation */}
      {!onboardingState?.is_completed && (
        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            Back
          </Button>
          
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/doctor-dashboard')}
              sx={{ mr: 2 }}
            >
              Save & Exit
            </Button>
            
            {activeStep < steps.length - 1 && (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNext}
                disabled={!isStepAccessible(activeStep + 1)}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EnhancedDoctorOnboarding;
